import PropTypes from 'prop-types';
import React, { useContext, useCallback } from 'react';
import ArticleAuthors from '../articleAuthors/articleAuthors';
import ArticleTitle from '../articleTitle/articleTitle';
import ArticlePublicationInfo from '../articlePublicationInfo/articlePublicationInfo';
import DoiArticleListItemActions from './actions/doiArticleListItemActions';
import PanelContext from '../sidebar/panel/panelContext';
import PiiArticleListItemActions from './actions/piiArticleListItemActions';
import { ArticleEventsPropTypes } from '../../utils/trackers/createArticleEvents';
import { analyticsFormat, analyticsScope, trackAdobeEvent } from '../../utils/trackers/adobe';

export default function ArticleListItem({
  authors,
  doi,
  articlePreviewPanel: component,
  articlePreviewProps: launchProps,
  eventData,
  onComponentClick,
  pageRange,
  pdfPath,
  pii,
  publicationDate,
  publicationName,
  title,
}) {
  const { openPanel } = useContext(PanelContext);
  const {
    preview,
    titleClick,
    viewPdf,
    savePdf,
    viewAtPublisher,
  } = eventData;

  const memoizedOpenPreview = useCallback((openerId) => {
    openPanel({ component, launchProps, openerId });

    if (preview) {
      trackAdobeEvent('buttonClick', preview);
    }

    if (onComponentClick) {
      onComponentClick({
        component: 'preview',
        format: analyticsFormat.MIME_HTML,
        scope: analyticsScope.ABSTRACT,
      });
    }
  }, [openPanel, component, launchProps, onComponentClick, preview]);

  return (
    <div className="article-list-item u-margin-l-bottom">
      <div className="article-list-item-title u-font-serif">
        <ArticleTitle
          pii={pii}
          doi={doi}
          title={title}
          titleClickEventData={titleClick}
          onClick={onComponentClick}
        />
      </div>

      <div className="article-list-item-authors text-xs">
        <ArticleAuthors authors={authors} />
      </div>

      <ArticlePublicationInfo
        pageRange={pageRange}
        publicationDate={publicationDate}
        publicationName={publicationName}
      />

      {pii && (
        <PiiArticleListItemActions
          pii={pii}
          pdfPath={pdfPath}
          openPreview={memoizedOpenPreview}
          viewPdfEventData={viewPdf}
          savePdfEventData={savePdf}
          onComponentClick={onComponentClick}
        />
      )}
      {
        !pii && doi
        && (
          <DoiArticleListItemActions
            doi={doi}
            openPreview={memoizedOpenPreview}
            viewAtPublisherEventData={viewAtPublisher}
          />
        )
      }
    </div>
  );
}

export const ArticleListItemPropTypes = {
  articlePreviewPanel: PropTypes.string,
  articlePreviewProps: PropTypes.shape({
    index: PropTypes.number,
    type: PropTypes.string,
    authorIndex: PropTypes.number,
    publicationIndex: PropTypes.number,
  }),
  authors: PropTypes.arrayOf(PropTypes.string).isRequired,
  doi: PropTypes.string,
  eventData: ArticleEventsPropTypes,
  onComponentClick: PropTypes.func,
  pageRange: PropTypes.arrayOf(PropTypes.string),
  pdfPath: PropTypes.string,
  pii: PropTypes.string,
  publicationDate: PropTypes.string,
  publicationName: PropTypes.string,
  title: PropTypes.node.isRequired,
};

ArticleListItem.propTypes = ArticleListItemPropTypes;

ArticleListItem.defaultProps = {
  articlePreviewPanel: undefined,
  articlePreviewProps: {},
  doi: undefined,
  eventData: {},
  pageRange: [],
  onComponentClick: undefined,
  pdfPath: undefined,
  pii: undefined,
  publicationDate: undefined,
  publicationName: undefined,
};
